import React, { useEffect, useState } from 'react';
import { node, bool, array, object, func } from 'prop-types';
import clsx from 'clsx';
import { IS_FOCUSSED, MODIFIER_IS_OPEN, IS_STANDALONE_LINK } from '../../constants';
import LinkColumn from '../LinkColumn';
import { StyledWrapperDiv, dropDownDetailsContent, menuHoverTriggerLink } from './styles';
import Link from '../Link';
import AnalyticsEventTypes from '../../utils/analyticsEvent';
import DropDownMenu from '../DropDownMenu';
import { columnChildren } from './utils';

/**
 * Component responsible for showing desktop dropdown items
 *  @param {object} props - Component props
 *  @param {React.Element} props.children - the child react component to render within our components
 *  @param {array} props.desktopColumns - Array of desktop column data
 *  @param {boolean} props.hasChildMenu - Boolean flag indicates if the menu has has children
 * @param {object} props.containerRef - Reference to the container element, let's us add a
 * mouseleave event listener so we can have an extra chance to close the menu
 */
const DesktopShoppingMenuDropdown = props => {
  const { desktopColumns, mainMenuLink, containerRef, setOpenMenuCount, topBarRef } = props;

  const [isMenuOpen, setMenuOpen] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const analyticsPayload = {
    props: { path: mainMenuLink?.path?.join(':'), hashKey: mainMenuLink?.hashKey },
    type: AnalyticsEventTypes.SHOPPING_EVENT,
  };

  useEffect(() => {
    setShowContent(isMenuOpen);
    setOpenMenuCount(prev => (isMenuOpen ? prev + 1 : prev ? prev - 1 : 0));
  }, [isMenuOpen]);

  // adding mouse events to close shopping menus more consistently
  useEffect(() => {
    const containerEventListener = containerRef.current?.addEventListener('mouseleave', () => {
      setMenuOpen(false);
    });
    const topBarEventListener = topBarRef.current?.addEventListener('mouseenter', () => {
      setMenuOpen(false);
    });
    return () => {
      containerRef.current?.removeEventListener('mouseleave', containerEventListener);
      topBarRef.current?.removeEventListener('mouseenter', topBarEventListener);
    };
  }, []);

  if (desktopColumns?.length <= 0) {
    return (
      <StyledWrapperDiv
        className={IS_STANDALONE_LINK}
        data-testid={'desktop-standalone-link-wrapper'}
      >
        <Link
          className={clsx(menuHoverTriggerLink, mainMenuLink?.className)}
          clickEventPayload={analyticsPayload}
          data-text={
            mainMenuLink?.className?.includes('glitch-effect') ? mainMenuLink?.label : undefined
          }
          href={mainMenuLink?.url}
        >
          {mainMenuLink?.label}
        </Link>
      </StyledWrapperDiv>
    );
  }

  return (
    <DropDownMenu
      MenuContainerComponent={StyledWrapperDiv}
      containerClassName={clsx(isMenuOpen && IS_FOCUSSED)}
      detailsContentCustomClass={clsx(dropDownDetailsContent, showContent && MODIFIER_IS_OPEN)}
      isMenuOpen={isMenuOpen}
      menuLabel={mainMenuLink?.label}
      menuLink={
        <Link
          className={clsx(menuHoverTriggerLink, mainMenuLink?.className, isMenuOpen && IS_FOCUSSED)}
          clickEventPayload={analyticsPayload}
          data-text={
            mainMenuLink?.className?.includes('glitch-effect') ? mainMenuLink?.label : undefined
          }
          href={mainMenuLink?.url}
        >
          {mainMenuLink?.label}
        </Link>
      }
      setMenuOpen={setMenuOpen}
      testId={'desktop-drop-down-styled-wrapper-div'}
    >
      {desktopColumns?.map((desktopColumn, index) => {
        desktopColumn.analyticsEventName = AnalyticsEventTypes.SHOPPING_EVENT;
        return (
          <LinkColumn
            columnChildren={columnChildren}
            content={desktopColumn}
            key={`${desktopColumn.label}-${index}`}
          />
        );
      })}
    </DropDownMenu>
  );
};

export default DesktopShoppingMenuDropdown;

DesktopShoppingMenuDropdown.propTypes = {
  children: node,
  hasChildMenu: bool,
  desktopColumns: array,
  mainMenuLink: object,
  containerRef: object,
  setOpenMenuCount: func,
  topBarRef: object,
};
